import { useState, useRef } from "react";
import { message, Modal } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import sortBy from "lodash/sortBy";
import db from "../../../../database";
import { addProductToCart } from "../cart";
import { TotalBillDiscount } from "../PricingRules/totalBillDiscount";
import { TotalBillFreeProductDiscount } from "../PricingRules/totalBillFreeProductDiscount";
import { CheckoutTotalManualDiscount } from "../PricingRules/CheckoutTotalManualDiscount";
import { CheckoutFlatDiscount } from "../PricingRules/CheckoutFlatDiscount";
import { CheckoutPercentageDiscount } from "../PricingRules/CheckoutPercentageDiscount";
import { getApplicablePrice } from "../../../../utils/getPriceList";
import { upsertLoyaltyTransaction, discardCouponService, upsertGiftCardTransaction } from "../../../../services";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import useSafeTimeout from "../../../../hooks/useSafeTimeout";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useAuthStore from "../../../../stores/useAuthStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { computeIsExchangeTarget } from "../../../../utils/exchangeCartRouting";
import dayjs from "dayjs";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { findPricingRuleById } from "../../../../utils/pricingRulesCache";
import { waitForProductsTableStable } from "../../../../utils/waitForProductsSync";
import useSyncProgressStore from "../../../../stores/useSyncProgressStore";
import { isPrepareBillModificationBlocked, PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE } from "../../../../utils/prepareBillModificationGuard";
import { advanceOrderFloorViolation } from "../../../../utils/advanceOrderValidation";

/**
 * useCartHandlers
 *
 * Extracts large cart-related handler functions from PointOfsaleCore:
 * - addProduct
 * - submitCustomAttributs
 * - modifyProductList
 * - processBillDiscounts
 * - handleSelectedSaleTypeForProducts
 * - clearReturnCartData
 * - adjustDiscount
 * - handleLoyaltyPayment / handleDeleteLoyaltyPayment
 * - selectOfferProduct
 * - handleVerifyPayment
 * - confirmDiscardCart
 * - changeCustomerId
 */
const useCartHandlers = ({
  cart,
  setCart,
  tillData,
  posConfig,
  precision,
  orderType,
  isExchange,
  tillLayout,
  cartRef,
  productsCopy,
  salesRepresent,
  salesRepresentDefaultLine,
  selectedProductInCart,
  setSelectedProductInCart,
  deleteCart,
  processTotalManualDiscount,
  setLoader,
  setSelectedRowKeys,
  setOrderTimeDetails,
  setDisplayAddOnSelection,
  displayAddOnSelection,
  handleAddOnModal,
  manualDiscountTypes,
  setProductStock,
  setProductBOMStock,
  setProductBOMStockData,
  setTableCards,
  dispatchItemSelection,
  searchInputRef,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  setManualDiscountInput,
  setSelectedManualDiscountTypeValue,
  removeAllDiscounts,
  setSelectedPaymentMethod,
  selectedReturnType,
  setSelectedAddons,
  setStockList,
  setProductSearchInput,
  setShowPaymentMethods,
  setDynamicForm,
  setDisplayBatchSelection,
  // Payment flow
  paymentFlow,
  giftCard,
  giftCardDataRef,
  getFilteredPaymentMethods,
  setShowPayments,
  setSelectedDocumentType,
  setOpen,
  // For handleSelectedSaleTypeForProducts
  setSelectedSaleTypeData,
  setSelectedSaleType,
  setIsSaleTypeFlag,
  setSaleTypeProducts,
  setShowProducts,
  setLoading,
  setCart: _setCart,
  // For clearReturnCartData
  giftCardData,
  // Refs
  addProductRef,
  processBillDiscountsRef,
  handleSelectedSaleTypeForProductsRef,
  handleLoyaltyPaymentRef,
  handleDeleteLoyaltyPaymentRef,
  // For addProduct
  receiptlessReturn2Enabled,
  // For handleVerifyPayment
  selectedPaymentMethod,
  amount,
  processPayment,
  authHeaders,
  // For confirmDiscardCart
  parkBill,
  history,
  // For selectOfferProduct
  setPaymentModalLocal,
  setDisplayOfferProductSelection,
  // For addOnsList target
  setOfferProductList,
  // Dispatch
  dispatchEvent,
  setOverPayedAmount,
  setGroupQty,
  setKeyValue,
  setCustomMixItems,
  setShowCustomMix,
  setIsInputFocused,
  removeCoupon,
}) => {
  const { safeSetTimeout } = useSafeTimeout();
  const [offerProductsList, setOfferProductListLocal] = useState();
  const [displayOfferProductSelectiton, setDisplayOfferProductSelectionLocal] = useState(false);
  const [comboBoxPopup, setComboBoxPopup] = useState(false);
  const loyaltyInFlightRef = useRef(false);

  const setOfferProductListFn = setOfferProductList || setOfferProductListLocal;
  const setDisplayOfferProductSelectionFn = setDisplayOfferProductSelection || setDisplayOfferProductSelectionLocal;

  // BUG-2020 (popup, not toast): a floor violation (edit/discount bringing a retrieved
  // Advance/Layaway order's total below what's already paid) is easy to miss as a
  // message.error toast -- reused here at both proactive-check sites (delete/qty-decrease
  // in addProduct, discount in processBillDiscounts) so the cashier gets a blocking modal
  // instead. Styled to match the existing "Cart Editing Restricted" Modal.confirm pattern
  // used elsewhere in this file for visual consistency.
  const showFloorViolationModal = (floorViolation) => {
    Modal.warning({
      title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Amount Cannot Be Reduced Below Paid Amount</div>,
      content: (
        <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
          <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>{floorViolation}</p>
        </div>
      ),
      icon: <WarningOutlined />,
      okText: "Ok",
      okButtonProps: { style: { display: "inline-block" } },
      modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
    });
  };

  const processBillDiscounts = (pricingRule, cartObj, iscoupon, couponInput, uniqReferenceId, mPricingCouponId, mPricingRulesId) => {
    if (pricingRule.type === "TD" && (pricingRule.manualDiscount === "N" || pricingRule.manualDiscount === null)) {
      // BUG-2020 (proactive floor check, discount path): same rule as addProduct's
      // delete/qty-decrease check above -- a discount that drops a retrieved
      // Advance/Layaway order's total below the amount already paid must be
      // rejected on the spot, not just caught later at final save
      // (processOrderApproved's own advanceOrderFloorViolation call). Checked
      // against the live store cart AFTER TotalBillDiscount runs (not the return
      // value) because TotalBillDiscount's own commit is conditional (skipped for
      // isExchange, and for the iscoupon-pending-validation branch) -- reading the
      // store's actual post-call state is correct regardless of which internal
      // branch fired.
      const preDiscountCartSnapshot = useCartStore.getState().cart;
      let updatedCart = TotalBillDiscount(pricingRule, setCart, cart, orderType, cartObj, iscoupon, couponInput, uniqReferenceId, mPricingCouponId, mPricingRulesId);
      if (!isExchange) {
        const liveCartAfterDiscount = useCartStore.getState().cart || {};
        const floorViolation = advanceOrderFloorViolation(liveCartAfterDiscount, liveCartAfterDiscount.total);
        if (floorViolation) {
          useCartStore.getState().setCart(preDiscountCartSnapshot);
          showFloorViolationModal(floorViolation);
          return preDiscountCartSnapshot;
        }
      }
      return updatedCart;
    }
    if (pricingRule.type === "TDF" && (pricingRule.manualDiscount === "N" || pricingRule.manualDiscount === null)) {
      let updatedCart = TotalBillFreeProductDiscount(
        pricingRule,
        setCart,
        cart,
        orderType,
        cartObj,
        (...args) => addProductRef.current?.(...args),
        setOfferProductListFn,
        setDisplayOfferProductSelectionFn,
        iscoupon,
        couponInput,
        uniqReferenceId,
        mPricingCouponId,
        mPricingRulesId
      );
      return updatedCart;
    }
  };

  const submitCustomAttributs = (prod) => {
    const productObj = { ...prod };

    // TSK-3303: custom-attribute confirmation calls addProductToCart directly
    // (bypasses addProduct's own gate above), so it needs its own check.
    if (isPrepareBillModificationBlocked(cart, posConfig)) {
      message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
      return;
    }
    // BUG-2020: same bypass -- gate on the till's "Allow Pre Order Update" flag,
    // matching addProduct's own gate above. docType==="Advance" covers "Retrieve Order",
    // scoped to /preorder (see Core/index.js's addon-repeat gate comment for why).
    if (
      (cart?.preOrder === "Y" || (cart?.docType === "Advance" && window.location.pathname.includes("/preorder"))) &&
      posConfig?.allowPreOrderUpdate !== "Y"
    ) {
      message.info("This is a pre-order with advance payment. Cart items cannot be modified at this stage. Please proceed to complete the transaction.");
      return;
    }

    addProductToCart(
      productObj,
      productObj.qty,
      false,
      cart,
      setCart,
      setSelectedProductInCart,
      deleteCart,
      processTotalManualDiscount,
      setLoader,
      salesRepresentDefaultLine,
      tillData,
      cartRef,
      productsCopy,
      salesRepresent,
      orderType,
      processBillDiscounts,
      undefined,
      setSelectedRowKeys,
      setOrderTimeDetails,
      setDisplayAddOnSelection,
      displayAddOnSelection,
      handleAddOnModal,
      manualDiscountTypes,
      CheckoutFlatDiscount,
      CheckoutPercentageDiscount,
      CheckoutTotalManualDiscount,
      setProductStock,
      selectedProductInCart,
      setTableCards,
      dispatchItemSelection,
      searchInputRef,
      false,
      setShowCompleteSaleStock,
      setCompleteSaleStockData,
      TotalBillDiscount,
      tillLayout,
      false,
      setManualDiscountInput,
      setSelectedManualDiscountTypeValue,
      removeAllDiscounts,
      setSelectedPaymentMethod,
      selectedReturnType,
      false,
      setProductBOMStock,
      setProductBOMStockData
    );
    setSelectedAddons([]);
  };

  const addProduct = async (addToCart, qty, totalQtyFlag, modifiedPrice, deleteProductInCart, quantityChange) => {
    setStockList([]);
    const returnThisReceipt = useTransactionStore.getState().returnThisReceipt;
    const isExchange = computeIsExchangeTarget({
      selectedReturnType: useTransactionStore.getState().selectedReturnType,
      returnThisReceipt,
      cart: useCartStore.getState().cart || cart,
    });
    const currentCart = (isExchange ? useCartStore.getState().exchangeCart : cart) || { ...cartRef.current };
    const mainCart = useCartStore.getState().cart || cart;
    if (receiptlessReturn2Enabled && currentCart?.isReturn === true && currentCart?.isBlindReceipt === true) {
      qty = -Math.abs(qty);
      addToCart.isReturn = true;
      addToCart.returnQty = Math.abs(qty);
    }
    if (currentCart.layAway === "Y" && posConfig?.allowLayawaywithoutPayment === "Y") {
      message.info("This order cannot be edited!");
    } else if (isPrepareBillModificationBlocked(currentCart, posConfig)) {
      // TSK-3303: once Prepare Bill has confirmed the order, item modifications
      // (add/qty/delete) are blocked unless the till explicitly allows them.
      message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
    } else {
      // BUG-2020: a retrieved Advance/Layaway cart with payment collected used to be
      // unconditionally blocked from ANY edit here (cart.preOrder === "Y"). Per the
      // ticket the cart should stay editable, gated only by the advance-payment floor
      // at processing time (see advanceOrderValidation.js, wired into
      // processOrderApproved in useOrderProcessing.js) -- not a blanket edit lock.
      // Made opt-in per till via the new Till Template flag "Allow Pre Order Update"
      // (posConfig.allowPreOrderUpdate) so existing tills keep today's locked
      // behavior until an admin turns it on; unset/"N" reproduces the original block.
      // docType==="Advance" covers "Retrieve Order" (ManagePreorder.js), which never
      // stamps preOrder -- see Core/index.js's addon-repeat gate comment for why this
      // pre-existing signal was reused instead of adding a new field, and why it's
      // scoped to /preorder (SMPurchaseReturnView.js sets the same docType value for
      // Chocolala's unrelated /pos Sales History retrieve).
      if (
        (currentCart?.preOrder === "Y" || (currentCart?.docType === "Advance" && window.location.pathname.includes("/preorder"))) &&
        posConfig?.allowPreOrderUpdate !== "Y"
      ) {
        Modal.confirm({
          title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Cart Editing Restricted</div>,
          content: (
            <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
              <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>
                This is a pre-order with advance payment. Cart items cannot be modified at this stage. Please proceed to complete the transaction.
              </p>
            </div>
          ),
          icon: <WarningOutlined />,
          okText: "Ok",
          cancelButtonProps: { style: { display: "none" } },
          autoFocusButton: null,
          okButtonProps: { style: { display: "inline-block" } },
          modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
          onOk() {
            const productSearchInput = document.getElementById("sm-product-search");
            if (productSearchInput) {
              safeSetTimeout(() => {
                productSearchInput.focus();
              }, 300);
            }
          },
        });
        return;
      }
      if (currentCart?.couponInput?.length > 0) {
        Modal.confirm({
          title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Cart Editing Restricted</div>,
          content: (
            <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
              <p style={{ marginBottom: 0 }}>Coupon applied to this order. Do you want to remove the coupon and continue editing the currentCart?</p>
            </div>
          ),
          icon: <WarningOutlined />,
          okText: "Yes",
          cancelText: "No",
          autoFocusButton: null,
          okButtonProps: { style: { display: "inline-block" } },
          onOk: async () => {
            if (currentCart?.couponInput?.length > 0) {
              for (const coupon of currentCart.couponInput) {
                await removeCoupon(coupon);
              }
            }
          },

          onCancel() {
            const productSearchInput = document.getElementById("sm-product-search");
            if (productSearchInput) {
              safeSetTimeout(() => {
                productSearchInput.focus();
              }, 300);
            }
          },
        });

        return;
      }
      if (Object.keys(addToCart).length === 0) {
        return;
      }

      if (!("isBlindReceipt" in currentCart) || (currentCart.isBlindReceipt && addToCart.isGiftCard)) {
        // Do nothing
      } else {
        setShowPaymentMethods(false);
      }

      setProductSearchInput("");
      localStorage.removeItem("productName");
      let freeFlag = true;
      if (addToCart.isFree) {
        const matchingPricingRules = findPricingRuleById(addToCart.mPricingruleId);
        const matchingProductIndex = matchingPricingRules[0]?.mPricingXProducts.find((op) => op.mProductId === addToCart.productId);
        freeFlag = matchingProductIndex && addToCart.weight + qty <= matchingProductIndex.quantity;
      }

      const findItemIndex = (condition) =>
        currentCart?.items?.findIndex(
          (p) =>
            p.productId === addToCart.productId &&
            p.upc === addToCart.upc &&
            p.mBatchId === addToCart.mBatchId &&
            p.isReturn === false &&
            p.lineId === addToCart.lineId &&
            condition(p)
        );

      const addonIndex = findItemIndex((p) => p?.selectedAddons?.length > 0);
      const attributeIndex = findItemIndex((p) => p?.newCustomAttributes?.length > 0);

      let addOnFlag = false;

      if (addToCart?.productAddons?.length > 0 && addToCart.weight + qty !== 0 && qty > 0 && addToCart.showAddons === "Y") {
        if (addonIndex !== -1) {
          addOnFlag = true;
        } else {
          setDisplayAddOnSelection(true);
          handleAddOnModal(addToCart, qty);
        }
      }

      if (addToCart?.customAttributes?.length > 0 && addToCart.weight + qty !== 0 && qty > 0) {
        if (attributeIndex !== -1) {
          addOnFlag = true;
        } else {
          setDynamicForm(true);
          addToCart.qty = qty;
          setSelectedProductInCart(addToCart);
        }
      }

      if (
        freeFlag &&
        ((addToCart?.productAddons?.length ?? 0) <= 0 || addToCart.weight + qty === 0 || qty < 0 || addOnFlag || addToCart.showAddons !== "Y") &&
        ((addToCart?.customAttributes?.length ?? 0) <= 0 || addToCart.weight + qty === 0 || qty < 0 || addOnFlag)
      ) {
        const preEditCartSnapshot = currentCart;
        await addProductToCart(
          addToCart,
          qty,
          totalQtyFlag,
          currentCart,
          setCart,
          setSelectedProductInCart,
          deleteCart,
          processTotalManualDiscount,
          setLoader,
          salesRepresentDefaultLine,
          tillData,
          cartRef,
          productsCopy,
          salesRepresent,
          orderType,
          processBillDiscounts,
          modifiedPrice,
          setSelectedRowKeys,
          setOrderTimeDetails,
          setDisplayAddOnSelection,
          displayAddOnSelection,
          handleAddOnModal,
          manualDiscountTypes,
          CheckoutFlatDiscount,
          CheckoutPercentageDiscount,
          CheckoutTotalManualDiscount,
          setProductStock,
          selectedProductInCart,
          setTableCards,
          dispatchItemSelection,
          searchInputRef,
          deleteProductInCart,
          setShowCompleteSaleStock,
          setCompleteSaleStockData,
          TotalBillDiscount,
          tillLayout,
          quantityChange,
          setManualDiscountInput,
          setSelectedManualDiscountTypeValue,
          removeAllDiscounts,
          setSelectedPaymentMethod,
          selectedReturnType,
          false,
          setProductBOMStock,
          setProductBOMStockData
        );
        // BUG-2020 (proactive floor check): a delete or qty-decrease on a retrieved
        // Advance/Layaway order that already has payment collected must be rejected
        // on the spot, not just at final save (advanceOrderFloorViolation was
        // previously wired only into processOrderApproved -- useOrderProcessing.js
        // -- letting the cart edit itself go through and only blocking the
        // completion click, which could leave the cashier staring at a nonsensical
        // negative remaining-due screen first).
        //
        // Widened scope: no longer gated on the GPR-only /preorder URL -- runs for
        // any retrieved Layaway/Preorder/Advance cart, any client, any module
        // (isAdvanceOrderWithPayment's own isRetrived/salesHistory check does the
        // scoping now). Exchange is excluded here explicitly -- the exchange target
        // is a different cart object (exchangeCart, the new items being exchanged
        // in) that never carries cart.isReturn, so isAdvanceOrderWithPayment's own
        // isReturn exclusion can't catch it; a return settled against a paid
        // Advance/Layaway order is the expected way its total drops below paid.
        if (!isExchange) {
          const liveCartAfterEdit = useCartStore.getState().cart || {};
          const floorViolation = advanceOrderFloorViolation(liveCartAfterEdit, liveCartAfterEdit.total);
          if (floorViolation) {
            useCartStore.getState().setCart(preEditCartSnapshot);
            showFloorViolationModal(floorViolation);
          }
        }
      }
    }
  };

  const modifyProductList = async () => {
    const result = await new Promise((resolve) => {
      const itemsToDispatch = selectedProductInCart;
      dispatchItemSelection(itemsToDispatch, cart, setCart, setProductStock, null, null, (eventData) => {
        if (eventData?.selectedItems?.length > 0) {
          resolve({
            selectedItems: eventData.selectedItems,
          });
        }
      });
    });
    selectedProductInCart.childItems = result?.selectedItems || [];

    setSelectedProductInCart({
      ...selectedProductInCart,
      childItems: result?.selectedItems,
    });

    const cartObj = cart || { items: [] };
    const updatedCartItems = cartObj.items.map((cartItem) => {
      if (cartItem.productId === selectedProductInCart.productId || cartItem.value === selectedProductInCart.value) {
        return {
          ...cartItem,
          childItems: result?.selectedItems,
        };
      }
      return cartItem;
    });

    setCart({
      ...cartObj,
      items: updatedCartItems,
    });
  };

  const handleSelectedSaleTypeForProducts = (obj) => async () => {
    try {
      setSelectedSaleTypeData(obj);
      const paymentDetails = getFilteredPaymentMethods(obj?.cwrSaletype?.cwrSaletypeId || null);
      setShowPayments(paymentDetails);
      usePOSConfigStore.getState().setSaleTypeData(obj);
      setSelectedSaleType(obj?.cwrSaletype?.name);
      setIsSaleTypeFlag(false);
      changeCustomerId(obj);

      const freshCart = useCartStore.getState().cart || cart;
      let updatedCart = { ...freshCart };
      updatedCart.customer = obj?.customer?.b2cCustomerId ? obj?.customer : tillData.tillAccess.csBunit.b2cCustomer;
      let processedProducts;
      const awaitingProductsSync = useSyncProgressStore.getState().productsTableSyncing;
      if (awaitingProductsSync) setLoading(true);
      try {
        await waitForProductsTableStable();
      } finally {
        if (awaitingProductsSync) setLoading(false);
      }
      // .filter() chained on db.products itself (not an already-materialized
      // array) serializes the predicate for IPC — SQLite mode (CW Mobile
      // Browser) has nothing to execute it and throws. Fetch, then filter in JS.
      let updatedProducts = (await db.products.toArray()).filter(
        (product) => product.isactive == true && product.productSegment !== "BG"
      );
      const filteredProducts = updatedProducts.filter((product) => product.priceList?.some((priceList) => priceList?.sPricelistId === updatedCart.customer?.sPriceListId));
      processedProducts = filteredProducts.map((product) => {
        const selectedPrice = getApplicablePrice({
          priceList: product.priceList,
          selectedUomId: product.uomData[0]?.csUomId,
          sPriceListId: updatedCart.customer.sPriceListId,
          alternateUom: product?.alternateUom || [],
          primaryUomId: product?.uom || null,
        });
        if (!selectedPrice) return product;
        return {
          ...product,
          price: selectedPrice.pricestd,
          salePrice: selectedPrice.pricestd,
          realPrice: selectedPrice.pricestd,
          sunitprice: selectedPrice.pricestd,
        };
      });
      if (!updatedCart?.customer?.sPriceListId) {
        processedProducts = updatedProducts;
      }

      setSaleTypeProducts(processedProducts);

      const finalCartObj = { ...updatedCart };
      setCart(finalCartObj);

      if (obj?.cwrSaletype?.fulfilmentType === "DI" || obj?.cwrSaletype?.fulfilmentType === "TA") {
        return;
      } else {
        setShowProducts(true);
      }
    } catch (e) {
    }
  };

  const clearReturnCartData = (record) => {
    if (cart.items.length > 0) {
      Modal.confirm({
        title: "Clear The Cart Items ?",
        icon: <ExclamationCircleOutlined />,
        content: <div>Do you want to clear the bill?</div>,
        okText: "Yes",
        cancelText: "No",
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        onOk() {
          let setAuthTokens;
          setSelectedDocumentType("Invoice");
          let PaymentMethods = getFilteredPaymentMethods() || [];
          let payments =
            PaymentMethods.length > 5
              ? sortBy(PaymentMethods, "sequenceNo").slice(
                  0,
                  tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
                )
              : PaymentMethods;

          setShowPayments(payments);
          const authHdrs = getOAuthHeaders();
          if (authHdrs && authHdrs.access_token) {
            setAuthTokens = authHdrs.access_token;
          }
          if (cart?.couponInput?.length > 0) {
            const couponsData = [...cart.couponInput];
            const discardCoupon = async (coupon) => {
              try {
                const response = await discardCouponService(coupon.couponCode, coupon.referenceId);
              } catch (error) {
                console.error(`Error discarding coupon ${coupon.couponCode}:`, error);
              }
            };
            couponsData.forEach((coupon) => {
              discardCoupon(coupon);
            });
          }
          let data = [];
          if (giftCardData.length > 0) {
            let details = [];
            giftCardData.map((ele) => {
              details.push(`{
                  cardNo: ${ele.number ? `"${ele.number}"` : null}
                  type: "TO"
                  referenceNo: "${cart.referenceId}"
                  amount: ${ele.amount * -1}
                  cardPin: ${data.pin ? `"${data.pin}"` : null}
                  tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
                  b2c_Customer_Id: ${cart.customer.cwrCustomerId ? `"${cart.customer.cwrCustomerId}"` : null}
                      }`);
            });
            data = {
              query: `
                  mutation {
                    upsertGiftCardTransaction(giftCardTransactions: [${details}]) {
                      status
                      message
                    }
                  }`,
            };
          }
          upsertGiftCardTransaction(data);
          let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
          orderTimeDetails = {
            ...orderTimeDetails,
            paymentStartTime: "",
            orderStartTime: "",
          };
          usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
          useCartStore.getState().setExchangeCart(null);
          useTransactionStore.getState().setSelectedReturnType(null);
          useTransactionStore.getState().setReturnThisReceipt(null);
          useTransactionStore.getState().setRefundReturnCustomerId(null);
          deleteCart(true, true);
          const productSearchInputEl = document.getElementById("sm-product-search");
          if (productSearchInputEl) {
            safeSetTimeout(() => {
              productSearchInputEl.focus();
              setOpen(false);
            }, 500);
          }
        },
        onCancel() {
          const productSearchInputEl = document.getElementById("sm-product-search");
          if (productSearchInputEl) {
            safeSetTimeout(() => {
              productSearchInputEl.focus();
              setOpen(false);
            }, 300);
          }
        },
      });
    }
  };

  const adjustDiscount = (cartObj) => {
    if (cartObj.items.length > 0 && cartObj.total > 0) {
      let cartTotal = cartObj.items.reduce((sum, item) => sum + item.nettotal + item.discount, 0);
      let newCartTotal = parseFloat(parseFloat(cartObj.total)?.toFixed(precision)) + parseFloat(parseFloat(cartObj.discount)?.toFixed(precision));
      if (parseFloat(parseFloat(newCartTotal)?.toFixed(precision)) !== parseFloat(parseFloat(cartTotal)?.toFixed(precision))) {
        let adjustAmount = newCartTotal - cartTotal;
        let lastDiscountedItemIndex = [...cartObj.items].reverse().findIndex((item) => item.discount > 0);
        if (lastDiscountedItemIndex !== -1) {
          let indexToUpdate = cartObj.items.length - 1 - lastDiscountedItemIndex;
          let updatedItems = [...cartObj.items];
          updatedItems[indexToUpdate].discount = parseFloat(parseFloat(updatedItems[indexToUpdate].discount - adjustAmount)?.toFixed(precision));
          updatedItems[indexToUpdate].nettotal = parseFloat(parseFloat(updatedItems[indexToUpdate].nettotal + adjustAmount)?.toFixed(precision));
          let updatedCart = {
            ...cartObj,
            items: updatedItems,
            discount: cartObj.discount - adjustAmount,
          };
          setCart(updatedCart);
        }
      }
    }
  };

  const handleLoyaltyPayment = async (payment, cartData) => {
    if (loyaltyInFlightRef.current) return;
    loyaltyInFlightRef.current = true;
    try {
      let setAuthTokens;
      const authHdrs = await getOAuthHeaders();
      if (authHdrs && authHdrs.access_token) {
        setAuthTokens = authHdrs.access_token;
      }
      const tillDataLocal = useAuthStore.getState().tillData;
      let value = parseFloat(parseFloat(payment.amount / cart.customer.redeemptionValue)?.toFixed(precision));
      if (cartData.customer.loyaltyLevel.cwrLoyaltyLevelId) {
        await upsertLoyaltyTransaction(tillDataLocal, value * -1, cartData.customer.cwrCustomerId, cartData.referenceId);
        cartData.customer.retlLoyaltyBalance = (cartData?.customer?.retlLoyaltyBalance || 0) + parseFloat(value);
      }
    } finally {
      loyaltyInFlightRef.current = false;
    }
  };

  const handleDeleteLoyaltyPayment = async (updatedCart, customer) => {
    let newCart = JSON.parse(JSON.stringify(updatedCart));
    newCart.customer = customer;
    const matchedPayment = updatedCart.payments.find((pay) => pay.name.toLowerCase() === "loyalty");
    if (!matchedPayment) return updatedCart;
    updatedCart.payments = updatedCart.payments.filter((pay) => pay.name.toLowerCase() !== "loyalty");
    updatedCart.paid = updatedCart.payments.reduce((sum, pay) => sum + parseFloat(pay.amount), 0);
    await handleLoyaltyPayment(matchedPayment, newCart);
    updatedCart.balance = parseFloat(parseFloat(updatedCart.total - updatedCart.paid)?.toFixed(precision));
    return updatedCart;
  };

  const selectOfferProduct = (product) => {
    const pro = product;
    addProduct(pro, 1, false, 0, false);
    setDisplayOfferProductSelectionFn(false);
    if (tillLayout === 2) {
      setPaymentModalLocal(true);
    }
  };

  const handleVerifyPayment = () => {
    let hostUrl = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
    let verifyPaytmUrl = `${hostUrl}paytm/verifyPayment`;
    const getPaymentSuccessValues = {
      midId: "Excelo34085435005810",
      orderId: `${cart.sOrderID}`,
      merchantKey: "qQUxrwRx@qE6zTxt",
      payUrlForVerify: "https://securegw-stage.paytm.in/v3/order/status",
      clientId: "C11",
      version: "v1",
    };
    const Axios = require("axios").default;
    Axios({
      url: verifyPaytmUrl,
      method: "POST",
      data: getPaymentSuccessValues,
      headers: {
        "Content-Type": "application/json",
        Authorization: `${authHeaders.access_token}`,
      },
    }).then((response) => {
      if (response.data.body.resultInfo.resultStatus === "TXN_SUCCESS") {
        if (paymentFlow) {
          paymentFlow.setPaytmQrCodeModalOpens?.(false);
        }
        processPayment(selectedPaymentMethod, amount);
      }
    });
  };

  const confirmDiscardCart = () => {
    if (cart.items.length > 0) {
      Modal.confirm({
        title: "Save Cart Items ?",
        className: "parkedItemsClass",
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            You can retrieve the bill later by selecting the 'Retrieve' option in Parked Bills.
            <br />
            Do you want to continue parking the bill?
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        onOk() {
          parkBill();
          safeSetTimeout(() => {
            history.push("/dashboard");
          }, 700);
        },
        onCancel: () => {
          history.push("/dashboard");
        },
      });
    } else {
      history.push("/dashboard");
    }
  };

  const changeCustomerId = (obj) => {
    let cartDetails = { ...cart, customer: { ...cart.customer } };
    cartDetails.customer.cwrCustomerId = obj.customer.b2cCustomerId;
    if (!isExchange) {
      setCart(cartDetails);
    }
  };

  // Wire refs
  if (addProductRef) addProductRef.current = addProduct;
  if (processBillDiscountsRef) processBillDiscountsRef.current = processBillDiscounts;
  if (handleSelectedSaleTypeForProductsRef) handleSelectedSaleTypeForProductsRef.current = handleSelectedSaleTypeForProducts;
  if (handleLoyaltyPaymentRef) handleLoyaltyPaymentRef.current = handleLoyaltyPayment;
  if (handleDeleteLoyaltyPaymentRef) handleDeleteLoyaltyPaymentRef.current = handleDeleteLoyaltyPayment;

  return {
    addProduct,
    submitCustomAttributs,
    modifyProductList,
    processBillDiscounts,
    handleSelectedSaleTypeForProducts,
    clearReturnCartData,
    adjustDiscount,
    handleLoyaltyPayment,
    handleDeleteLoyaltyPayment,
    selectOfferProduct,
    handleVerifyPayment,
    confirmDiscardCart,
    changeCustomerId,
    offerProductsList,
    setOfferProductList: setOfferProductListFn,
    displayOfferProductSelectiton,
    setDisplayOfferProductSelection: setDisplayOfferProductSelectionFn,
    comboBoxPopup,
    setComboBoxPopup,
  };
};

export default useCartHandlers;
